// =============================================================================
// src/app/access.ts — single source of truth for role → screen authorization
// =============================================================================
// Phase 1 of docs/portal-architecture-refactor-plan.md, plus the D-A/D-B
// reconciliation ratified by the Dev Team on 2026-06-12.
//
// Before this module, the same "which role may see which screen" rule was
// encoded in FOUR places that had to agree but were maintained separately
// (portal.jsx page gates, Header.jsx nav flags, sessionUser.defaultPageForUser,
// and unused enums.ts helpers). It is now defined ONCE here and exhaustively
// tested in access.test.ts (every (role, sessionMode) × screen cell).
//
// ONE rule per screen. canAccess (what a user may render — the authoritative,
// security-relevant rule, also used by future route guards) and navItemsFor
// (which top-nav buttons show) are now derived from the SAME table, so the nav
// can never drift from access again.
//
// The model: a screen is gated on the user's EFFECTIVE ROLE — the job they are
// actually doing. A COMBINED user picks a mode at login (PostLoginSelection);
// that choice IS their role for the session. So "picker mode" == picker,
// "responder mode" == responder, full stop.
//
// Ratified access matrix (effective role → screens):
//   shelter              → My Requests
//   responder (= staff,  → Responders Task List, All Requests
//     or combined in
//     responder mode)
//   picker (= picker     → Pickers Task List
//     role, or combined
//     in picker mode)
//   admin                → everything
//
// This intentionally CHANGED two pre-existing bugs (see access.test.ts):
//   D-A) A plain responder could previously render the Pickers page (deep-link /
//        fall-through). Pickers is now picker/admin only.
//   D-B) A combined user in picker mode was previously shown Responders + All
//        Requests buttons (Header hard-coded COMBINED as staff) that fell through
//        to the wrong screen. Such a user now sees only the Pickers Task List.
//   Also: admin tools (Reports/Admin/Users/Import/Audit) are now admin-ROLE only;
//        a combined user in responder mode no longer sees them.
// =============================================================================

import { ROLES } from "../utils/enums";

export type ScreenId =
  | "dashboard"
  | "todo"
  | "pickers"
  // Stock Top-ups approval queue (stock-top-ups plan §4). A matrix-grantable module
  // (off by default), shown top-level as a sibling of the Pickers list — NOT the
  // Responders list.
  | "top_up_tasks"
  | "reports"
  | "statistics"
  | "admin"
  | "templates"
  | "users"
  | "staff"
  | "import"
  | "invites"
  | "push_queue"
  | "audit"
  | "suppliers"
  | "schools"
  | "lookups"
  | "setup"
  | "resource_content"
  // Capability-only module (no NAV screen): gates the Calendar create/edit controls
  // on the Resources → Calendar view. Matrix-grantable like the other admin modules.
  | "calendar_manage"
  // Capability-only module (no NAV screen): gates who may APPROVE/DECLINE items on a
  // MONITORED ("refer to management") DFV Service. Matrix-grantable, OFF by default —
  // a Supervisor without it is held to Needs Info like a responder; a SuperUser is
  // always exempt. See docs/permissions-and-approval-hold-plan.md §Part 1.
  | "monitored_approval"
  | "products"
  | "product_map"
  | "so_rules"
  | "sales_orders"
  // Stock Top-ups Sales-Order-Sync pair (Manage → Sales Order Sync): the editable
  // Top-up Product Mapping + Top-up SO Rules.
  | "top_up_product_map"
  | "top_up_so_rules"
  | "crm"
  | "telemarketing"
  | "feedback"
  // Resources menu (all internal staff): read-only staff directory + calendar +
  // supervisor-authored reference sections.
  | "staff_list"
  | "calendar"
  | "general"
  | "invoicing"
  | "responders"
  // SuperUser-only: the per-admin permissions matrix (docs/superuser-role-plan.md)
  | "permissions";

export interface AccessUser {
  role?: string | null;
  sessionMode?: string | null;
  // Per-admin module permission set (the 11 admin ScreenIds this admin may use).
  // NULL/undefined = fall back to DEFAULT_ADMIN_PERMISSIONS. Only meaningful for
  // the `admin` (reduced "Supervisor") role; SuperUsers always have everything.
  adminPermissions?: string[] | null;
}

// "Effective role" collapses (role, sessionMode) into the job the user is doing.
// A COMBINED user always has a sessionMode by the time they reach the
// authenticated shell (sessionReady gates on PostLoginSelection), so this is
// well-defined for every reachable input. ROLES.PICKER ("picker") and sessionMode
// "picker" coincide by design, so a picker-role and a picker-mode user both
// collapse to "picker".
export type EffectiveRole =
  | "admin"
  | "superuser"
  | "staff"
  | "picker"
  | "shelter"
  | "responder"
  | null;

export function effectiveRole(user: AccessUser | null | undefined): EffectiveRole {
  if (!user) return null;
  if (user.sessionMode === "responder") return "responder";
  if (user.sessionMode === "picker") return "picker";
  return (user.role ?? null) as EffectiveRole;
}

// ── Role-group predicates (the ratified job model) ───────────────────────────
// These double as the "is the current viewer acting as X" check that screen
// components use to gate in-screen controls, so they are null-safe and exported.
//
// SUPERUSER (added 7 July 2026) is the top internal tier and has EVERYTHING an
// admin has, so it is included wherever "admin" grants a power. The Admin↔
// SuperUser split for the 11 admin MODULES is handled separately, per-account,
// by adminModulePermitted() below — NOT by these job predicates.

// The new top tier (full access; the only role that can edit the matrix).
export const isSuperUser = (user: AccessUser | null | undefined): boolean =>
  effectiveRole(user) === ROLES.SUPERUSER;
// "Admin-level or above" — the admin role OR a SuperUser. This is the gate for
// admin-only IN-SCREEN controls (the monitored-service flag + approval-hold
// exemption, feedback "review all", the Manage dropdown). NOT combined-in-
// responder-mode. (The reduced Admin still counts here; individual MODULE access
// is filtered per-account by adminModulePermitted.)
export const isAdminRole = (user: AccessUser | null | undefined): boolean => {
  const r = effectiveRole(user);
  return r === ROLES.ADMIN || r === ROLES.SUPERUSER;
};
// Doing the responder job: the staff role, an admin, a SuperUser, or a combined
// user in responder mode. Screen components gate staff-only controls on it.
export const isResponderJob = (user: AccessUser | null | undefined): boolean => {
  const r = effectiveRole(user);
  return r === ROLES.STAFF || r === ROLES.ADMIN || r === ROLES.SUPERUSER || r === "responder";
};
// Doing the picker job: the picker role / picker mode, or an admin / SuperUser.
// Used by the Pickers screen access rule (an admin oversees everything) and by
// RequestDetail to show the picker view when a picker-job user opens a request
// FROM the Pickers List.
export const isPickerJob = (user: AccessUser | null | undefined): boolean => {
  const r = effectiveRole(user);
  return r === ROLES.PICKER || r === ROLES.ADMIN || r === ROLES.SUPERUSER;
};
// Acting specifically AS a picker (picker role or picker mode) — admin EXCLUDED.
// Distinct from isPickerJob: used where an admin must NOT be treated as a picker
// (e.g. RequestDetail hides the approval panel from pickers but keeps it for admins).
export const isActingAsPicker = (user: AccessUser | null | undefined): boolean =>
  effectiveRole(user) === ROLES.PICKER;
const isShelter = (user: AccessUser): boolean => effectiveRole(user) === ROLES.SHELTER;
// Any internal Zephyr user (admin / staff / picker / responder-or-picker mode) —
// i.e. anyone who is NOT a shelter caseworker. Exported for ReportsPage's MFA
// messaging, which applies to all internal users (pickers included), so it is
// deliberately broader than isResponderJob.
export const isInternalUser = (user: AccessUser | null | undefined): boolean => {
  const r = effectiveRole(user);
  return r !== null && r !== ROLES.SHELTER;
};

// ── ADMIN MODULES + per-admin permissions (SuperUser matrix) ─────────────────
// The 11 admin MODULES the per-admin permissions matrix governs (docs/
// superuser-role-plan.md). A SuperUser can reach all of them; a reduced Admin
// reaches only the modules in their saved set (or the default when NULL).
export const ADMIN_MODULES: ScreenId[] = [
  "reports",
  "templates",
  "users",
  "staff",
  "import",
  "invites",
  "push_queue",
  "audit",
  "suppliers",
  "schools",
  "crm",
  "telemarketing",
  "feedback",
  "lookups",
  "products",
  "product_map",
  "so_rules",
  "sales_orders",
  // Stock Top-ups Sales-Order-Sync pair — matrix-grantable, OFF by default.
  "top_up_product_map",
  "top_up_so_rules",
  // Stock Top-ups approval queue (stock-top-ups plan §4) — matrix-grantable, OFF by
  // default: a SuperUser sees it; a reduced Admin (Supervisor) only when granted.
  "top_up_tasks",
  // Resources content, matrix-grantable, OFF by default (John, 26 Jul 2026): a
  // reduced Admin gets these only when a SuperUser ticks them.
  "resource_content", // Resources Management — the Resource Pages editor screen
  "calendar_manage", // Calendar Management — create/edit on the Resources → Calendar view
  "monitored_approval", // Approve Monitored DFV Services — gates monitored-service approval
];
// NOTE: "setup" (System Setup) is intentionally NOT a grantable admin module — it
// is SuperUser-only (see SCREEN_ACCESS below), so a reduced Admin can never be
// granted it via the matrix. (John, 25 Jul 2026.)
// Default modules a reduced Admin starts with when they have no custom set
// (profiles.admin_permissions IS NULL). EMPTY by decision (John, 6 Sep 2026): a new
// Supervisor gets NO Manage modules by default — a SuperUser grants each in the matrix.
// (The responder-level screens — Responders Task List / All Requests / Resources — come
// from the role, NOT this list, so a Supervisor still does the responder job.)
// ⚠️ Existing Supervisors previously on this default (reports/users/staff/crm/feedback)
// must be re-granted those modules explicitly around deploy — see
// docs/permissions-and-approval-hold-plan.md §Part 2.
export const DEFAULT_ADMIN_PERMISSIONS: ScreenId[] = [];
// May this user reach a given admin MODULE?
//  - SuperUser: always (full access).
//  - Admin: only if the module is in their saved set (or the default when NULL).
//  - everyone else: no.
function adminModulePermitted(user: AccessUser, screen: ScreenId): boolean {
  if (isSuperUser(user)) return true;
  if (effectiveRole(user) !== ROLES.ADMIN) return false;
  const perms = user.adminPermissions ?? DEFAULT_ADMIN_PERMISSIONS;
  return perms.includes(screen);
}

// ── ACCESS ────────────────────────────────────────────────────────────────
// The authoritative "may this user render this screen?" table — one rule each.
const SCREEN_ACCESS: Record<ScreenId, (user: AccessUser) => boolean> = {
  dashboard: isShelter, // My Requests — shelter caseworkers
  todo: isResponderJob, // Responders Task List
  // All Requests — responders, plus pickers (db-177 gives them read access; they open
  // a request here into the read-only ShelterRequestView, not the picker processing view).
  admin: (u) => isResponderJob(u) || isPickerJob(u),
  pickers: isPickerJob, // Pickers Task List
  // Stock Top-ups approval queue — matrix-grantable (off by default), not a job role.
  top_up_tasks: (u) => adminModulePermitted(u, "top_up_tasks"),
  // The 11 admin modules — SuperUser always; reduced Admin per their matrix set.
  reports: (u) => adminModulePermitted(u, "reports"),
  // Statistics dashboard — split out of Reports on 2 Sep 2026. It rides the SAME
  // "reports" grant (NOT a separately-grantable ADMIN_MODULE), so whoever can see
  // Reports also sees Statistics and no per-admin matrix migration is needed.
  statistics: (u) => adminModulePermitted(u, "reports"),
  templates: (u) => adminModulePermitted(u, "templates"),
  users: (u) => adminModulePermitted(u, "users"),
  staff: (u) => adminModulePermitted(u, "staff"),
  import: (u) => adminModulePermitted(u, "import"),
  invites: (u) => adminModulePermitted(u, "invites"),
  audit: (u) => adminModulePermitted(u, "audit"),
  suppliers: (u) => adminModulePermitted(u, "suppliers"),
  schools: (u) => adminModulePermitted(u, "schools"),
  lookups: (u) => adminModulePermitted(u, "lookups"),
  push_queue: (u) => adminModulePermitted(u, "push_queue"),
  // System Setup — SuperUser ONLY (John, 25 Jul 2026). Holds the platform wiring
  // + the Access lockout switches, so it is not delegable to a reduced Admin.
  setup: isSuperUser,
  // Resource Pages editor (Manage → "Resources Management") — authors the
  // Resources → Invoicing/Responders content. Matrix-grantable, OFF by default
  // (John, 26 Jul 2026): SuperUser always; a reduced Admin only when granted.
  // Viewing the content is a separate internal-staff right (invoicing/responders).
  resource_content: (u) => adminModulePermitted(u, "resource_content"),
  // Calendar Management — a capability (no screen of its own): gates create/edit
  // on the Resources → Calendar view. Matrix-grantable, OFF by default.
  calendar_manage: (u) => adminModulePermitted(u, "calendar_manage"),
  // Approve Monitored DFV Services — a capability (no screen): gates who may
  // approve/decline items on a monitored ("refer to management") service. Matrix-
  // grantable, OFF by default. Enforced in approvalHold.ts + its call sites.
  monitored_approval: (u) => adminModulePermitted(u, "monitored_approval"),
  products: (u) => adminModulePermitted(u, "products"), // Unleashed → Products (catalogue)
  product_map: (u) => adminModulePermitted(u, "product_map"), // Unleashed → Product Mapping
  so_rules: (u) => adminModulePermitted(u, "so_rules"), // Unleashed → SO Rules
  sales_orders: (u) => adminModulePermitted(u, "sales_orders"), // Unleashed → Sales Orders
  // Top-up Requests pair (Sales Order Sync) — matrix-grantable admin modules.
  top_up_product_map: (u) => adminModulePermitted(u, "top_up_product_map"),
  top_up_so_rules: (u) => adminModulePermitted(u, "top_up_so_rules"),
  crm: (u) => adminModulePermitted(u, "crm"), // opens in its own tab
  telemarketing: (u) => adminModulePermitted(u, "telemarketing"), // standalone tab module
  // Feedback — shelter workers log/review their OWN; admins/SuperUsers with the
  // feedback module review ALL. Opens in its own tab. (docs/feedback-module-plan.md)
  feedback: (u) => isShelter(u) || adminModulePermitted(u, "feedback"),
  // Resources menu — all internal staff (responders, pickers, admins) may view.
  // Supervisors + SuperUsers author the content.
  //
  // Authoring is gated IN-COMPONENT by the matrix modules (resource_content /
  // calendar_manage). RLS independently restricts writes on resource_pages /
  // resource_documents / calendar_events to role='admin' (superuser folded in) — so it
  // blocks staff, pickers, combined and every caseworker, but it does NOT enforce the
  // per-admin matrix. A Supervisor without the module ticked is stopped by the UI only.
  // (This comment used to claim the matrix itself was enforced by RLS. Finding B13.)
  staff_list: isInternalUser,
  calendar: isInternalUser,
  general: isInternalUser,
  invoicing: isInternalUser,
  responders: isInternalUser,
  // The Supervisor Permissions matrix editor. Grantable via the "Zephyr Staff"
  // module (John, 7 Sep 2026): ticking Zephyr Staff in the matrix gives a Supervisor
  // the full editor (SuperUsers pass adminModulePermitted anyway). ⚠️ This is the one
  // grant that can hand out every OTHER module, so it is the segregation-of-duties
  // point of the whole model — see docs/permissions-model-review.md.
  permissions: (u) => adminModulePermitted(u, "staff"),
};

export function canAccess(user: AccessUser, screen: ScreenId): boolean {
  return SCREEN_ACCESS[screen](user);
}

// ── NAV ─────────────────────────────────────────────────────────────────────
// Top-nav buttons, in display order. Visibility is now exactly canAccess — a
// user only ever sees a button for a screen they can actually use. `id` equals
// the portal `page`/`setPage` key, so navItemsFor feeds Header directly.
//
// `kind` tells the Header how to render the item: "page" = an in-app setPage
// switch; "tab" = a self-contained module that opens /<id> in a NEW browser tab
// (CRM, Feedback).
export type NavKind = "page" | "tab";
export interface NavItem {
  id: ScreenId;
  label: string;
  kind: NavKind;
}
// A dropdown grouping under one heading (e.g. "Manage"). `items` may itself
// contain nested groups (one level) — e.g. Manage → "Unleashed" → Sales Orders,
// rendered as a flyout sub-menu. `groupScreen` (optional) is the screen the
// sub-group heading itself navigates to when it is ALSO a page (e.g. "DFV
// Services" opens the DFV Services list AND flies out its tools).
export interface NavGroup {
  group: string;
  items: (NavItem | NavGroup)[];
  groupScreen?: NavItem;
}
export type NavEntry = NavItem | NavGroup;
// Narrowing guard for consumers (Header): true when an entry is a dropdown group.
export const isNavGroup = (e: NavEntry): e is NavGroup => "group" in e;

const NAV: NavItem[] = [
  { id: "dashboard", label: "My Requests", kind: "page" },
  { id: "todo", label: "Responders Task List", kind: "page" },
  { id: "pickers", label: "Pickers Task List", kind: "page" },
  { id: "top_up_tasks", label: "Top-up Task List", kind: "page" },
  { id: "admin", label: "All Requests", kind: "page" },
  { id: "reports", label: "Reports", kind: "page" },
  { id: "statistics", label: "Statistics", kind: "page" },
  { id: "templates", label: "System Content", kind: "page" },
  // "Shelters & Users" split on 5 July 2026 (shelter-admin redesign Phase 1):
  // `users` is the shelters list (the interim Caseworkers tab was retired in
  // Phase 3 — caseworkers are managed on the shelter detail's Users tab);
  // `staff` is the standalone Zephyr Staff console.
  { id: "users", label: "DFV Services", kind: "page" },
  { id: "staff", label: "Zephyr Staff", kind: "page" },
  { id: "import", label: "Import Unleashed Data", kind: "page" },
  { id: "invites", label: "Invitations", kind: "page" },
  { id: "push_queue", label: "DFV Services Push List", kind: "page" },
  { id: "audit", label: "Audit Log", kind: "page" },
  { id: "suppliers", label: "Suppliers", kind: "page" },
  { id: "schools", label: "Schools", kind: "page" },
  { id: "lookups", label: "Dropdown Lists", kind: "page" },
  { id: "setup", label: "System Setup", kind: "page" },
  { id: "resource_content", label: "Resources Management", kind: "tab" },
  { id: "products", label: "Products", kind: "page" },
  { id: "product_map", label: "Standard Product Mapping", kind: "page" },
  { id: "so_rules", label: "Standard SO Rules", kind: "page" },
  { id: "sales_orders", label: "Sales Orders", kind: "page" },
  { id: "top_up_product_map", label: "Top-up Product Mapping", kind: "page" },
  { id: "top_up_so_rules", label: "Top-up SO Rules", kind: "page" },
  { id: "crm", label: "DFV Services Communication", kind: "tab" },
  { id: "telemarketing", label: "Telemarketing", kind: "tab" },
  { id: "feedback", label: "Client Feedback", kind: "tab" },
  // Resources menu (all internal staff) — open in a new tab (kind "tab").
  { id: "staff_list", label: "Staff List", kind: "tab" },
  { id: "calendar", label: "Calendar", kind: "tab" },
  { id: "general", label: "General", kind: "tab" },
  { id: "invoicing", label: "Invoicing", kind: "tab" },
  { id: "responders", label: "Responders", kind: "tab" },
  // SuperUser-only per-admin permissions matrix (docs/superuser-role-plan.md).
  { id: "permissions", label: "Supervisor Permissions", kind: "page" },
];

// Flat list of every nav item the user may use (access === visibility, no drift).
// Kept as the canonical "what can this user reach" list; navStructureFor arranges
// the same items for display (top-level + the admin "Manage" dropdown).
export function navItemsFor(user: AccessUser): NavItem[] {
  return NAV.filter((n) => canAccess(user, n.id));
}

// ── NAV STRUCTURE (display arrangement) ──────────────────────────────────────
// What the Header renders: top-level buttons + (for admins) a single "Manage"
// dropdown holding ALL the admin tools ("Shelters & Users" + "System Content"
// (templates) moved in on 2 July 2026 — John's call — so an admin's top level is
// just the three request screens + Manage). Feedback is top-level for a SHELTER
// worker (shelters have no Manage menu) but lives INSIDE Manage for an admin —
// and since any user is either shelter or admin, never both, it shows exactly
// once. (docs/feedback-module-plan.md §4)
const MANAGE_LABEL = "Manage";
// Alphabetical by LABEL (John's call, 3 July 2026), headings included — e.g.
// Applications, Audit Log, DFV Services, Reports & Statistics, … Client Feedback,
// DFV Services Communication and Telemarketing now live under the "Applications"
// heading. Keep this ordering alphabetical when adding new tools.
// A Manage-dropdown entry is either a plain admin screen id, or a flyout
// sub-group. A sub-group has a heading label, its child screen `ids`, and an
// optional `screen` the heading itself navigates to when it is also a page
// (DFV Services, Zephyr Staff). Ordered alphabetically by the label shown
// (headings included) — keep it alphabetical when adding tools.
type ManageEntry = ScreenId | { subGroup: string; screen?: ScreenId; ids: ScreenId[] };
const MANAGE_STRUCTURE: ManageEntry[] = [
  // "Applications" heading flies out the self-contained modules that each open in
  // a new tab (Client Feedback, DFV Services Communication, Telemarketing).
  { subGroup: "Applications", ids: ["feedback", "crm", "telemarketing"] },
  "audit", // Audit Log
  // "DFV Services" heading opens the DFV Services list (users) + flies out its tools.
  {
    subGroup: "DFV Services",
    screen: "users",
    ids: ["lookups", "import", "invites", "push_queue"],
  },
  "reports", // Reports (CSV builder)
  "resource_content", // Resources Management
  "schools", // Schools
  "statistics", // Statistics (dashboard) — rides the reports grant; alphabetical by label
  "suppliers", // Suppliers
  "templates", // System Content
  "setup", // System Setup
  // Sales Order Sync holds the standard (fit-out) SO tools AND the Stock Top-ups
  // Product Mapping + SO Rules (folded in here on 13 Aug 2026 — John's call — so
  // there is no separate "Top-up Requests" flyout). The screens/modules stay
  // isolated; only the menu placement is shared.
  {
    subGroup: "Sales Order Sync",
    ids: [
      "products",
      "product_map",
      "so_rules",
      "sales_orders",
      "top_up_product_map",
      "top_up_so_rules",
    ],
  },
  // "Zephyr Staff" heading opens the staff console + flies out the Supervisor
  // Permissions matrix (both granted by the "Zephyr Staff" module — 7 Sep 2026).
  { subGroup: "Zephyr Staff", screen: "staff", ids: ["permissions"] },
];
// Display order of the items that are NOT in the Manage group (feedback included
// here for its shelter-only top-level placement; skipped for admins below).
const TOP_LEVEL_ORDER: ScreenId[] = [
  "dashboard",
  "todo",
  "pickers",
  "top_up_tasks",
  "admin",
  "feedback",
];

function navItemById(id: ScreenId): NavItem {
  // Every NAV-listed ScreenId resolves here. The only ScreenId without a NAV entry
  // is the capability-only "calendar_manage" — it is never passed to this function
  // (it appears only in ADMIN_MODULES / SCREEN_ACCESS, not in any display group).
  return NAV.find((n) => n.id === id) as NavItem;
}

export function navStructureFor(user: AccessUser): NavEntry[] {
  const admin = isAdminRole(user);
  const entries: NavEntry[] = [];
  for (const id of TOP_LEVEL_ORDER) {
    if (!canAccess(user, id)) continue;
    // Feedback is top-level only for non-admins; admins get it inside Manage.
    if (id === "feedback" && admin) continue;
    entries.push(navItemById(id));
  }
  if (admin) {
    const items: (NavItem | NavGroup)[] = [];
    for (const entry of MANAGE_STRUCTURE) {
      if (typeof entry === "string") {
        if (canAccess(user, entry)) items.push(navItemById(entry));
        continue;
      }
      // Flyout sub-group: the child screens this user can reach, plus (if the
      // heading is itself a page) the own-screen the heading navigates to.
      const subItems = entry.ids.filter((id) => canAccess(user, id)).map(navItemById);
      const ownScreen =
        entry.screen && canAccess(user, entry.screen) ? navItemById(entry.screen) : undefined;
      if (subItems.length > 0) {
        items.push({ group: entry.subGroup, groupScreen: ownScreen, items: subItems });
      } else if (ownScreen) {
        // No reachable sub-tools → just the heading's own screen as a plain item
        // (keeps a reduced Admin's menu free of empty flyouts).
        items.push(ownScreen);
      }
    }
    if (items.length > 0) entries.push({ group: MANAGE_LABEL, items });
  }
  // Resources dropdown — a general reference menu for all internal staff (separate
  // from the admin-only Manage menu). Currently the Staff List directory + Calendar.
  const resourceItems: NavItem[] = [];
  for (const id of ["staff_list", "calendar", "general", "invoicing", "responders"] as ScreenId[]) {
    if (canAccess(user, id)) resourceItems.push(navItemById(id));
  }
  if (resourceItems.length > 0) entries.push({ group: "Resources", items: resourceItems });
  return entries;
}

// ── BREADCRUMBS ──────────────────────────────────────────────────────────────
// A "Manage › Unleashed › SO Rules" trail for the current screen, derived from the
// same NAV / MANAGE_STRUCTURE source of truth. A crumb is either a GROUP (a menu
// level — `siblings` are the sibling pages to jump between, rendered as a popover)
// or a PAGE (`screen`, a link). The last crumb is `current`. Empty for a top-level
// request screen or a non-nav path (no breadcrumb shown).
// The mode-agnostic Resources screens (a general internal-staff reference menu).
// Exported so SessionGate can let a combined user reach one of these in a fresh tab
// WITHOUT being forced to pick a responder/picker mode first (they don't depend on it).
export const RESOURCE_NAV_IDS: ScreenId[] = [
  "staff_list",
  "calendar",
  "general",
  "invoicing",
  "responders",
];

export interface BreadcrumbCrumb {
  label: string;
  screen?: ScreenId; // a navigable page (link)
  siblings?: NavItem[]; // a menu level → the sibling pages to offer in a popover
  current?: boolean; // the leaf (the screen you are on)
}

// The accessible pages at the top level of the Manage menu (plain tools + the two
// sub-group headings that are themselves pages). Unleashed has no own page, so it
// contributes none here — its pages appear under the "Unleashed" crumb instead.
function manageLevelPages(user: AccessUser): NavItem[] {
  const out: NavItem[] = [];
  for (const entry of MANAGE_STRUCTURE) {
    if (typeof entry === "string") {
      if (canAccess(user, entry)) out.push(navItemById(entry));
    } else if (entry.screen && canAccess(user, entry.screen)) {
      out.push(navItemById(entry.screen));
    }
  }
  return out;
}

// The accessible pages inside a Manage sub-group (its own screen, if any, then its
// tools) — the "Unleashed" / "DFV Services" / "Zephyr Staff" popover contents.
function subGroupPages(
  user: AccessUser,
  entry: { subGroup: string; screen?: ScreenId; ids: ScreenId[] }
): NavItem[] {
  const out: NavItem[] = [];
  if (entry.screen && canAccess(user, entry.screen)) out.push(navItemById(entry.screen));
  for (const id of entry.ids) if (canAccess(user, id)) out.push(navItemById(id));
  return out;
}

export function breadcrumbTrailFor(user: AccessUser, screen: ScreenId): BreadcrumbCrumb[] {
  // Resources menu screens (internal staff only).
  if (isInternalUser(user) && RESOURCE_NAV_IDS.includes(screen)) {
    return [
      {
        label: "Resources",
        siblings: RESOURCE_NAV_IDS.filter((id) => canAccess(user, id)).map(navItemById),
      },
      { label: navItemById(screen).label, screen, current: true },
    ];
  }
  // Manage menu screens are admin-only — a non-admin never gets a "Manage › …"
  // trail (e.g. a shelter's top-level Client Feedback is NOT under Manage for them).
  if (!isAdminRole(user)) return [];
  for (const entry of MANAGE_STRUCTURE) {
    if (typeof entry === "string") {
      if (entry === screen) {
        return [
          { label: MANAGE_LABEL, siblings: manageLevelPages(user) },
          { label: navItemById(screen).label, screen, current: true },
        ];
      }
      continue;
    }
    // Sub-group: the screen is either the heading's own page or one of its tools.
    if (entry.screen === screen) {
      // The sub-group heading page itself (DFV Services, Zephyr Staff).
      return [
        { label: MANAGE_LABEL, siblings: manageLevelPages(user) },
        { label: navItemById(screen).label, screen, current: true },
      ];
    }
    if (entry.ids.includes(screen)) {
      return [
        { label: MANAGE_LABEL, siblings: manageLevelPages(user) },
        { label: entry.subGroup, siblings: subGroupPages(user, entry) },
        { label: navItemById(screen).label, screen, current: true },
      ];
    }
  }
  // Top-level request screen / non-nav path → no breadcrumb.
  return [];
}

// ── DEFAULT LANDING ──────────────────────────────────────────────────────────
// The screen a user lands on after login. Every persona's default is within its
// own access set (verified in access.test.ts). sessionUser.defaultPageForUser
// delegates here so there is a single definition.
export function defaultScreenFor(user: AccessUser): "todo" | "pickers" | "dashboard" {
  if (isResponderJob(user)) return "todo";
  if (isPickerJob(user)) return "pickers";
  return "dashboard";
}
